import type { Game, GameAction, GamePhase, Player } from '../types'
import { pickBilingual, text } from '../i18n'

export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
export const RECOVERY_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'

export function normalizeRoomCode(value: string): string {
  return Array.from(value.toUpperCase())
    .filter((character) => ROOM_CODE_ALPHABET.includes(character))
    .join('')
    .slice(0, 4)
}

export function generateRoomCode(): string {
  const bytes = new Uint32Array(4)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (value) => ROOM_CODE_ALPHABET[value % ROOM_CODE_ALPHABET.length]).join('')
}

export function normalizeRecoveryCode(value: string): string {
  return Array.from(value.toUpperCase())
    .filter((character) => RECOVERY_CODE_ALPHABET.includes(character))
    .join('')
    .slice(0, 12)
}

export function generateRecoveryCode(): string {
  const bytes = new Uint32Array(12)
  crypto.getRandomValues(bytes)
  const compact = Array.from(bytes, (value) => RECOVERY_CODE_ALPHABET[value % RECOVERY_CODE_ALPHABET.length]).join('')
  return compact.match(/.{1,4}/g)!.join('-')
}

export async function hashRecoveryCode(value: string): Promise<string> {
  const normalized = normalizeRecoveryCode(value)
  if (normalized.length !== 12) throw new Error('Recovery code must contain 12 characters · קוד השחזור צריך להכיל 12 תווים.')
  const bytes = new TextEncoder().encode(normalized)
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

export function parseChipAmount(value: string | number): number | null {
  const amount = typeof value === 'number' ? value : Number(value.replace(/,/g, ''))
  return Number.isSafeInteger(amount) && amount > 0 ? amount : null
}

export function formatChips(value: number): string {
  return new Intl.NumberFormat(text('en-US', 'he-IL')).format(value)
}

export function gamePhase(game: Pick<Game, 'phase'>): GamePhase {
  return game.phase ?? 'playing'
}

export function bettingRound(game: Pick<Game, 'bettingRound'>): number {
  return game.bettingRound ?? 1
}

export function roundContribution(game: Game, player: Player): number {
  if (player.contributionHand !== game.handNumber) return 0
  if (game.bettingRound == null || player.contributionRound == null) {
    return player.handContribution
  }
  return player.contributionRound === game.bettingRound ? (player.roundContribution ?? 0) : 0
}

export function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

export function actionText(action: GameAction): string {
  switch (action.type) {
    case 'create_game':
      return pickBilingual(`${action.actorName} opened the table with ${formatChips(action.amount)} chips/player · ${action.actorName} פתח/ה שולחן עם ${formatChips(action.amount)} צ׳יפים לשחקן`)
    case 'join':
      return pickBilingual(`${action.actorName} joined · ${action.actorName} הצטרף/ה למשחק`)
    case 'join_table':
      return pickBilingual(`${action.actorName} opened table view · ${action.actorName} פתח/ה תצוגת שולחן`)
    case 'start_game':
      return pickBilingual(`${action.actorName} said everyone is in · ${action.actorName} אישר/ה שכולם בפנים`)
    case 'set_leader':
      return pickBilingual(`${action.actorName} made ${action.targetName} the Leader · ${action.actorName} מינה/תה את ${action.targetName} למוביל/ה`)
    case 'restart_game':
      return pickBilingual(`${action.actorName} returned everyone to the lobby · ${action.actorName} החזיר/ה את כולם ללובי`)
    case 'new_round':
      return pickBilingual(`${action.actorName} started betting round ${action.amount} · ${action.actorName} פתח/ה סבב הימורים ${action.amount}`)
    case 'bet':
      return pickBilingual(`${action.actorName} added ${formatChips(action.amount)} to the pot · ${action.actorName} הוסיף/ה ${formatChips(action.amount)} לקופה`)
    case 'check':
      return pickBilingual(`${action.actorName} checked · ${action.actorName} עשה/תה צ׳ק`)
    case 'fold':
      return pickBilingual(`${action.actorName} folded · ${action.actorName} פרש/ה מהיד`)
    case 'award':
      return pickBilingual(`${action.actorName} awarded ${formatChips(action.amount)} to ${action.targetName} · ${action.actorName} העניק/ה ${formatChips(action.amount)} ל${action.targetName}`)
    case 'new_hand':
      return pickBilingual(`${action.actorName} started a new hand · ${action.actorName} התחיל/ה יד חדשה`)
    case 'reset_hand':
      return pickBilingual(`${action.actorName} reset the broken hand and refunded ${formatChips(action.amount)} to ${action.playerCount ?? 0} players · ${action.actorName} איפס/ה את היד התקולה והחזיר/ה ${formatChips(action.amount)} ל־${action.playerCount ?? 0} שחקנים`)
    case 'adjust_balance':
      return pickBilingual(`${action.actorName} adjusted ${action.targetName} by ${action.amount > 0 ? '+' : ''}${formatChips(action.amount)} · ${action.actorName} תיקן/ה ל${action.targetName} ${action.amount > 0 ? '+' : ''}${formatChips(action.amount)} צ׳יפים`)
    case 'adjust_pot':
      return pickBilingual(`${action.actorName} adjusted the pot by ${action.amount > 0 ? '+' : ''}${formatChips(action.amount)} · ${action.actorName} תיקן/ה את הקופה ב־${action.amount > 0 ? '+' : ''}${formatChips(action.amount)}`)
    case 'undo_bet':
      return pickBilingual(`${action.actorName} reversed ${formatChips(action.amount)} from ${action.targetName} · ${action.actorName} ביטל/ה תרומה של ${formatChips(action.amount)} מ${action.targetName}`)
    case 'undo_fold':
      return pickBilingual(`${action.actorName} reversed ${action.targetName}'s fold · ${action.actorName} ביטל/ה את הכיפול של ${action.targetName}`)
    case 'reclaim_table':
      return pickBilingual(`${action.actorName} recovered table control · ${action.actorName} שחזר/ה את בקרת השולחן`)
    case 'set_recovery':
      return pickBilingual(`${action.actorName} renewed the recovery code · ${action.actorName} חידש/ה את קוד השחזור`)
  }
}

export function actionIcon(type: GameAction['type']): string {
  switch (type) {
    case 'bet':
      return '↗'
    case 'check':
      return '✓'
    case 'fold':
      return '×'
    case 'award':
      return '★'
    case 'new_hand':
      return '↻'
    case 'start_game':
      return '▶'
    case 'set_leader':
      return '♛'
    case 'restart_game':
      return '⟲'
    case 'new_round':
      return '◉'
    case 'reset_hand':
      return '⟲'
    case 'adjust_balance':
    case 'adjust_pot':
      return '±'
    case 'undo_bet':
    case 'undo_fold':
      return '↶'
    case 'reclaim_table':
      return '⌁'
    case 'set_recovery':
      return '◇'
    case 'create_game':
      return '●'
    case 'join':
    case 'join_table':
      return '+'
  }
}

export function relativeActionTime(action: GameAction, now = Date.now()): string {
  const milliseconds = action.createdAt?.toMillis()
  if (!milliseconds) return text('NOW', 'עכשיו')
  const minutes = Math.max(0, Math.floor((now - milliseconds) / 60_000))
  if (minutes < 1) return text('NOW', 'עכשיו')
  if (minutes < 60) return text(`${minutes} MIN AGO`, `לפני ${minutes} דק׳`)
  return new Intl.DateTimeFormat(text('en-US', 'he-IL'), { hour: '2-digit', minute: '2-digit' }).format(milliseconds)
}

export interface UndoEligibility {
  allowed: boolean
  action: GameAction | null
  reason: string
}

export function getUndoEligibility(game: Game, actions: GameAction[], players: Player[]): UndoEligibility {
  const latest = actions.find((action) => action.id === game.lastActionId) ?? null
  if (!latest) return { allowed: false, action: null, reason: 'Latest action is still syncing · הפעולה האחרונה עדיין מסתנכרנת.' }
  if (!game.handOpen) return { allowed: false, action: latest, reason: 'Hand closed; use table correction · היד נסגרה; השתמשו בתיקון.' }
  if (latest.handNumber !== game.handNumber) return { allowed: false, action: latest, reason: 'Action belongs to a previous hand · הפעולה שייכת ליד קודמת.' }

  const target = players.find((player) => player.uid === latest.targetUid)
  if (latest.type === 'bet') {
    const contribution = target?.contributionHand === game.handNumber ? target.handContribution : 0
    const currentRoundContribution = target ? roundContribution(game, target) : 0
    if (!target || game.pot < latest.amount || contribution < latest.amount || currentRoundContribution < latest.amount) {
      return { allowed: false, action: latest, reason: 'Contribution has a dependent change; use correction · יש שינוי תלוי; השתמשו בתיקון.' }
    }
    return { allowed: true, action: latest, reason: `Undo ${formatChips(latest.amount)} from ${target.name} · ביטול ${formatChips(latest.amount)} של ${target.name}` }
  }

  if (latest.type === 'fold') {
    if (!target || target.foldedHand !== game.handNumber) {
      return { allowed: false, action: latest, reason: 'Fold is no longer current · הכיפול כבר אינו המצב הנוכחי.' }
    }
    return { allowed: true, action: latest, reason: `Undo ${target.name}'s fold · ביטול הכיפול של ${target.name}` }
  }

  return { allowed: false, action: latest, reason: 'Only the latest bet or fold can be undone · רק התרומה או הכיפול האחרונים ניתנים לביטול.' }
}
